import React, { useEffect, useRef, useState } from 'react';
import './SyncedVideoPlayer.css';

const SyncedVideoPlayer = ({ 
  videoUrl, 
  youtubeUrl, 
  currentTime, 
  isPlaying, 
  onTimeUpdate,
  onDurationChange,
  videoOffset = 0,
  playerId: propsPlayerId,
}) => {
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [youtubeReady, setYoutubeReady] = useState(false);
  const [containerReady, setContainerReady] = useState(false);
  const intervalRef = useRef(null);
  const videoOffsetRef = useRef(videoOffset);
  const isYouTube = !!youtubeUrl;
  const generatedId = React.useId();
  const playerId = propsPlayerId || `yt-${generatedId.replace(/:/g, '')}`;

  // Keep videoOffset in ref for callback access
  useEffect(() => {
    videoOffsetRef.current = videoOffset;
  }, [videoOffset]);

  // Only mount YouTube iframe when container has non-zero size (fixes black panel)
  useEffect(() => {
    if (!isYouTube) return;
    setContainerReady(false);
    let rafId;
    const scheduleReady = () => {
      rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => setContainerReady(true));
      });
    };
    const el = wrapperRef.current;
    if (!el) {
      scheduleReady();
      return () => cancelAnimationFrame(rafId);
    }
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          scheduleReady();
          break;
        }
      }
    });
    ro.observe(el);
    if (el.offsetWidth > 0 && el.offsetHeight > 0) scheduleReady();
    const fallback = setTimeout(scheduleReady, 500);
    return () => {
      ro.disconnect();
      clearTimeout(fallback);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isYouTube, youtubeUrl]);

  // Extract YouTube video ID (11-char standard; fallback for short IDs)
  const extractYouTubeID = (url) => {
    if (url == null || typeof url !== 'string') return '';
    const u = url.trim();
    if (!u) return '';
    const strict = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/;
    let match = u.match(strict);
    if (match) return match[1];
    const loose = /(?:[?&]v=)([^&]+)|youtu\.be\/([^?&#]+)/;
    match = u.match(loose);
    const id = (match[1] || match[2] || '').trim();
    return id ? id.slice(0, 11) : '';
  };

  // Initialize YouTube IFrame API (runs when container is ready so iframe exists)
  useEffect(() => {
    if (!isYouTube || !containerReady) return;

    // Load YouTube IFrame API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Initialize player when API is ready
    const initPlayer = () => {
      if (window.YT && window.YT.Player && iframeRef.current) {
        playerRef.current = new window.YT.Player(iframeRef.current, {
          events: {
            onReady: (event) => {
              setYoutubeReady(true);
              if (onDurationChange) {
                onDurationChange(event.target.getDuration());
              }
              // Start interval to update current time
              intervalRef.current = setInterval(() => {
                if (playerRef.current && playerRef.current.getCurrentTime) {
                  const videoTime = playerRef.current.getCurrentTime();
                  // Add videoOffset back to convert to global timeline
                  const globalTime = videoTime + videoOffsetRef.current;
                  // Only update if video is actually playing (not stuck at 0 before offset)
                  if (videoTime > 0 || videoOffsetRef.current === 0) {
                    onTimeUpdate(globalTime);
                  }
                }
              }, 100);
            },
            onStateChange: (event) => {
              // YouTube player state changed
            }
          }
        });
      }
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isYouTube, youtubeUrl, containerReady, onTimeUpdate, onDurationChange]);

  // Sync currentTime to YouTube player
  useEffect(() => {
    if (isYouTube && youtubeReady && playerRef.current && playerRef.current.seekTo) {
      // Apply video offset: video stays at 0 until currentTime passes videoOffset
      if (currentTime < videoOffset) {
        // Before offset: keep video at 0:00 and paused
        if (playerRef.current.getCurrentTime?.() > 0) {
          playerRef.current.seekTo(0, true);
        }
        if (playerRef.current.getPlayerState?.() === 1) {
          playerRef.current.pauseVideo();
        }
      } else {
        // After offset: play video at adjusted time
        const adjustedTime = currentTime - videoOffset;
        const currentVideoTime = playerRef.current.getCurrentTime?.() || 0;
        if (Math.abs(currentVideoTime - adjustedTime) > 1) {
          playerRef.current.seekTo(adjustedTime, true);
        }
      }
    }
  }, [currentTime, isYouTube, youtubeReady, videoOffset]);

  // Sync play/pause state to YouTube player
  useEffect(() => {
    if (isYouTube && youtubeReady && playerRef.current) {
      const player = playerRef.current;
      
      // Check current player state to avoid redundant calls
      // Only play if currentTime is past videoOffset
      if (isPlaying && currentTime >= videoOffset) {
        if (player.playVideo && player.getPlayerState && player.getPlayerState() !== 1) {
          player.playVideo();
        }
      } else {
        if (player.pauseVideo && player.getPlayerState && player.getPlayerState() === 1) {
          player.pauseVideo();
        }
      }
    }
  }, [isPlaying, isYouTube, youtubeReady, currentTime, videoOffset]);

  // Sync currentTime to video element (non-YouTube)
  useEffect(() => {
    if (!isYouTube && videoRef.current) {
      // Apply video offset: video stays at 0 until currentTime passes videoOffset
      if (currentTime < videoOffset) {
        // Before offset: keep video at 0:00
        if (videoRef.current.currentTime > 0) {
          videoRef.current.currentTime = 0;
        }
      } else {
        // After offset: play video at adjusted time
        const adjustedTime = currentTime - videoOffset;
        if (Math.abs(videoRef.current.currentTime - adjustedTime) > 0.5) {
          videoRef.current.currentTime = adjustedTime;
        }
      }
    }
  }, [currentTime, isYouTube, videoOffset]);

  // Sync play/pause state (non-YouTube)
  useEffect(() => {
    if (!isYouTube && videoRef.current) {
      const video = videoRef.current;
      
      // Only play if currentTime is past videoOffset
      if (isPlaying && currentTime >= videoOffset) {
        if (video.paused) {
          video.play().catch(err => console.log('Play error:', err));
        }
      } else {
        if (!video.paused) {
          video.pause();
        }
      }
    }
  }, [isPlaying, isYouTube, currentTime, videoOffset]);

  // Handle video time updates (non-YouTube)
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const videoTime = videoRef.current.currentTime;
      // Add videoOffset back to convert to global timeline
      const globalTime = videoTime + videoOffset;
      // Only update if video is actually playing (not stuck at 0 before offset)
      if (videoTime > 0 || videoOffset === 0) {
        onTimeUpdate(globalTime);
      }
    }
  };

  // Handle duration loaded (non-YouTube)
  const handleLoadedMetadata = () => {
    if (videoRef.current && onDurationChange) {
      onDurationChange(videoRef.current.duration);
    }
  };

  if (isYouTube) {
    let youtubeId = extractYouTubeID(youtubeUrl);
    if (!youtubeId && videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'))) {
      youtubeId = extractYouTubeID(videoUrl);
    }
    if (!youtubeId) {
      return (
        <div className="synced-video-player youtube youtube-invalid">
          <span>Invalid or unsupported video URL</span>
        </div>
      );
    }
    return (
      <div ref={wrapperRef} className="synced-video-player youtube">
        {containerReady && (
          <iframe
            ref={iframeRef}
            id={playerId}
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${youtubeId}?enablejsapi=1`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
    );
  }

  if (!videoUrl || (typeof videoUrl === 'string' && !videoUrl.trim())) {
    return (
      <div className="synced-video-player synced-video-player-no-source">
        <span>No video source</span>
      </div>
    );
  }

  return (
    <div className="synced-video-player">
      <video
        ref={videoRef}
        src={videoUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        className="video-element"
      />
    </div>
  );
};

export default SyncedVideoPlayer;

